/**
 * The web analogue of the macOS menu bar dropdown — restyled to sit
 * cohesively inside Koyal Studio (warm surface, soft radii, a single
 * pixel-font accent on the KOKO title) instead of clashing as a second
 * pixel-art UI. Hosts the meaningful, editable settings:
 *
 *   - Session active
 *   - Hey Koko wake word
 *   - Text-only mode
 *   - Voice (TTS)
 *   - Sound effects
 *   - Proactive observations + interval stepper (1–30 min)
 *   - Koko mode (Action / Suggestion)  ← moved here from page chrome
 *   - Session memory: always on; "Edit" opens the memory content
 *   - Nest when idle + idle-seconds stepper (30 s minimum + default)
 *   - Director playbook: view + edit `director.md` inline
 *   - WORKFLOWS tab (2026-07-04 yap): a separate tab listing Koko's
 *     saved, reusable multi-step routines — view, edit, save new,
 *     delete, reset to the shipped starter set
 *   - Hotkey hints footer
 *
 * Removed (now fixed sensible defaults, per the 2026-06-26 yap):
 *   - "Show Koko" (session active = present)
 *   - Web search toggle (always on under the hood)
 *   - Model picker (Opus by default)
 *   - Session memory ON/OFF toggle (memory is always on)
 *
 * Each row reads and writes through the shared `SettingsStore` so
 * changes persist to localStorage and broadcast to every subscriber
 * (proactive observer, nest timer, KokoApp pipeline, etc.). The Koko
 * mode + director-playbook rows are studio concerns wired through
 * `KokoInteractionMode` + `DirectorPlaybookStore`.
 */

import type { SettingsStore } from "../app/SettingsStore";
import type { BirdStyleOption } from "../sprite/birdVariants";
import type { WakeWordStatus, WakeWordStatusKind } from "../input/WakeWordListener";
import {
  getKokoInteractionMode,
  setKokoInteractionMode,
  subscribeKokoInteractionMode,
  type KokoInteractionMode,
} from "../studio/KokoInteractionMode";
import {
  areWorkflowsCustomized,
  deleteWorkflow,
  getWorkflows,
  isDirectorPlaybookCustomized,
  type KokoWorkflow,
  readDirectorPlaybookForEditor,
  resetDirectorPlaybook,
  resetWorkflows,
  saveWorkflow,
  subscribeDirectorPlaybook,
  subscribeWorkflows,
  writeDirectorPlaybook,
} from "../studio/DirectorPlaybookStore";

const POSITION_TICK_MS = 1000 / 60;
const PANEL_GAP_ABOVE_ANCHOR_PX = 14;

export interface ControlPanelOptions {
  getAnchorPoint: () => { x: number; y: number };
  settings: SettingsStore;
  wakeWordSupported: boolean;
  birdStyles: readonly BirdStyleOption[];
  onEditMemory: () => void;
}

export class ControlPanel {
  private readonly panelElement: HTMLDivElement;
  private readonly options: ControlPanelOptions;
  private positionTimerId: number | null = null;
  private outsideClickListener: ((event: MouseEvent) => void) | null = null;
  private keyDownListener: ((event: KeyboardEvent) => void) | null = null;
  private wakeWordStatusDot: HTMLSpanElement | null = null;
  private wakeWordStatusLabel: HTMLSpanElement | null = null;
  private wakeWordTranscriptsList: HTMLOListElement | null = null;
  private wakeWordStatusRow: HTMLDivElement | null = null;
  private wakeWordTranscriptsBox: HTMLDivElement | null = null;
  // Tabbed layout (SETTINGS | WORKFLOWS) + workflows-tab state.
  private settingsTabButton: HTMLButtonElement | null = null;
  private workflowsTabButton: HTMLButtonElement | null = null;
  private settingsTabElement: HTMLDivElement | null = null;
  private workflowsTabElement: HTMLDivElement | null = null;
  private workflowsListElement: HTMLDivElement | null = null;
  private workflowsStatusElement: HTMLSpanElement | null = null;
  private workflowsResetButton: HTMLButtonElement | null = null;
  private isDraftingNewWorkflow = false;

  constructor(options: ControlPanelOptions) {
    this.options = options;
    this.panelElement = document.createElement("div");
    this.panelElement.className = "koko-pixel-box koko-control-panel";
    this.renderContents();
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.panelElement);
  }

  show(): void {
    this.positionToAnchor();
    this.panelElement.classList.add("visible");
    this.startPositionTimer();
    this.installDismissListeners();
  }

  hide(): void {
    this.panelElement.classList.remove("visible");
    this.stopPositionTimer();
    this.removeDismissListeners();
  }

  isVisible(): boolean {
    return this.panelElement.classList.contains("visible");
  }

  toggle(): void {
    if (this.isVisible()) this.hide();
    else this.show();
  }

  destroy(): void {
    this.hide();
    this.panelElement.remove();
  }

  private renderContents(): void {
    const fillLayer = document.createElement("div");
    fillLayer.className = "koko-pixel-box-fill";

    fillLayer.appendChild(this.buildTitle());
    fillLayer.appendChild(this.buildTabBar());

    const settingsTab = document.createElement("div");
    settingsTab.className = "koko-tab-content";
    settingsTab.dataset.tabContent = "settings";
    this.settingsTabElement = settingsTab;

    const workflowsTab = document.createElement("div");
    workflowsTab.className = "koko-tab-content";
    workflowsTab.dataset.tabContent = "workflows";
    workflowsTab.hidden = true;
    this.workflowsTabElement = workflowsTab;
    this.buildWorkflowsTab(workflowsTab);

    fillLayer.appendChild(settingsTab);
    fillLayer.appendChild(workflowsTab);

    this.populateSettingsTab(settingsTab);

    this.panelElement.appendChild(fillLayer);
  }

  private populateSettingsTab(fillLayer: HTMLDivElement): void {
    fillLayer.appendChild(
      this.buildToggleRow({
        label: "Session active",
        hint: "turn Koko off entirely without removing the widget",
        settingKey: "sessionActive",
      }),
    );
    fillLayer.appendChild(
      this.buildToggleRow({
        label: "Hey Koko",
        hint: this.options.wakeWordSupported
          ? "always-on wake word"
          : "needs Chrome or Edge — your browser doesn't support wake word",
        settingKey: "wakeWordEnabled",
        disabled: !this.options.wakeWordSupported,
      }),
    );
    fillLayer.appendChild(this.buildWakeWordStatusRow());
    fillLayer.appendChild(this.buildWakeWordTranscriptsBox());
    this.applyWakeWordRowVisibility();
    this.options.settings.subscribe("wakeWordEnabled", () => {
      this.applyWakeWordRowVisibility();
    });
    const birdStyleRow = this.buildBirdStyleRow();
    if (birdStyleRow) fillLayer.appendChild(birdStyleRow);
    fillLayer.appendChild(
      this.buildToggleRow({
        label: "Text-only mode",
        hint: "show Koko's replies in a text panel, no TTS",
        settingKey: "textOnlyMode",
      }),
    );
    fillLayer.appendChild(
      this.buildToggleRow({
        label: "Voice (TTS)",
        hint: "play ElevenLabs audio for Koko's replies",
        settingKey: "voiceEnabled",
      }),
    );
    fillLayer.appendChild(
      this.buildToggleRow({
        label: "Sound effects",
        hint: "tiny chirps on listen, think, done, etc.",
        settingKey: "sfxEnabled",
      }),
    );
    fillLayer.appendChild(
      this.buildToggleAndStepperRow({
        label: "Proactive observations",
        hint: "Koko checks in unprompted",
        toggleKey: "proactiveEnabled",
        stepperKey: "proactiveIntervalMinutes",
        stepperMin: 1,
        stepperMax: 30,
        stepperSuffix: "min",
      }),
    );
    fillLayer.appendChild(this.buildInteractionModeRow());
    fillLayer.appendChild(this.buildMemoryRow());
    fillLayer.appendChild(
      this.buildToggleAndStepperRow({
        label: "Nest when idle",
        hint: "fly to a corner after inactivity (30 s minimum)",
        toggleKey: "nestEnabled",
        stepperKey: "nestIdleSeconds",
        stepperMin: 30,
        stepperMax: 1800,
        stepperStep: 30,
        stepperSuffix: "s",
      }),
    );
    fillLayer.appendChild(this.buildDirectorPlaybookSection());
    fillLayer.appendChild(this.buildFooter());
  }

  /** SETTINGS | WORKFLOWS tab strip under the panel title. */
  private buildTabBar(): HTMLDivElement {
    const bar = document.createElement("div");
    bar.className = "koko-tabbar";

    const makeTab = (tabId: "settings" | "workflows", label: string) => {
      const tab = document.createElement("button");
      tab.type = "button";
      tab.className = "koko-tab";
      tab.dataset.tab = tabId;
      tab.textContent = label;
      tab.addEventListener("click", () => this.selectTab(tabId));
      bar.appendChild(tab);
      return tab;
    };
    this.settingsTabButton = makeTab("settings", "SETTINGS");
    this.workflowsTabButton = makeTab("workflows", "WORKFLOWS");
    this.settingsTabButton.classList.add("selected");
    return bar;
  }

  private selectTab(tabId: "settings" | "workflows"): void {
    const isSettings = tabId === "settings";
    this.settingsTabButton?.classList.toggle("selected", isSettings);
    this.workflowsTabButton?.classList.toggle("selected", !isSettings);
    if (this.settingsTabElement) this.settingsTabElement.hidden = !isSettings;
    if (this.workflowsTabElement) this.workflowsTabElement.hidden = isSettings;
  }

  // ------------------------------------------------------------------
  // Workflows tab — list / view / edit / save / new / delete / reset
  // ------------------------------------------------------------------

  private buildWorkflowsTab(container: HTMLDivElement): void {
    const hint = document.createElement("div");
    hint.className = "koko-workflows-hint";
    hint.textContent =
      "Reusable multi-step routines Koko can run on request — try “run the ‘Storyboard arc 1’ workflow”. Edits apply to Koko's next turn.";
    container.appendChild(hint);

    const list = document.createElement("div");
    list.className = "koko-workflows-list";
    container.appendChild(list);
    this.workflowsListElement = list;

    const toolbar = document.createElement("div");
    toolbar.className = "koko-workflows-toolbar";

    const status = document.createElement("span");
    status.className = "koko-workflows-status";
    this.workflowsStatusElement = status;

    const spacer = document.createElement("span");
    spacer.style.flex = "1";

    const resetButton = document.createElement("button");
    resetButton.type = "button";
    resetButton.className = "koko-pill-button koko-workflows-reset";
    resetButton.textContent = "RESET STARTERS";
    resetButton.addEventListener("click", () => resetWorkflows());
    this.workflowsResetButton = resetButton;

    const newButton = document.createElement("button");
    newButton.type = "button";
    newButton.className = "koko-pill-button is-primary koko-workflow-new";
    newButton.textContent = "NEW WORKFLOW";
    newButton.addEventListener("click", () => {
      this.isDraftingNewWorkflow = true;
      this.renderWorkflowsList();
    });

    toolbar.appendChild(status);
    toolbar.appendChild(spacer);
    toolbar.appendChild(resetButton);
    toolbar.appendChild(newButton);
    container.appendChild(toolbar);

    subscribeWorkflows(() => this.renderWorkflowsList());
    this.renderWorkflowsList();
  }

  private renderWorkflowsList(): void {
    const list = this.workflowsListElement;
    if (!list) return;
    list.replaceChildren();

    if (this.isDraftingNewWorkflow) {
      list.appendChild(
        this.buildWorkflowEditor(null, () => {
          this.isDraftingNewWorkflow = false;
          this.renderWorkflowsList();
        }),
      );
    }

    const workflows = getWorkflows();
    for (const workflow of workflows) {
      list.appendChild(this.buildWorkflowItem(workflow));
    }

    if (this.workflowsStatusElement) {
      const customized = areWorkflowsCustomized();
      this.workflowsStatusElement.textContent = customized
        ? `${workflows.length} workflows · customized`
        : `${workflows.length} starter workflows`;
    }
    if (this.workflowsResetButton) {
      this.workflowsResetButton.disabled = !areWorkflowsCustomized();
    }
  }

  private buildWorkflowItem(workflow: KokoWorkflow): HTMLDivElement {
    const item = document.createElement("div");
    item.className = "koko-workflow-item";
    item.dataset.workflowId = workflow.id;

    const header = document.createElement("button");
    header.type = "button";
    header.className = "koko-workflow-open";
    const name = document.createElement("span");
    name.className = "koko-workflow-name";
    name.textContent = workflow.name;
    const meta = document.createElement("span");
    meta.className = "koko-workflow-meta";
    meta.textContent = `${workflow.steps.length} steps`;
    header.appendChild(name);
    header.appendChild(meta);
    item.appendChild(header);

    let editorElement: HTMLDivElement | null = null;
    header.addEventListener("click", () => {
      if (editorElement) {
        editorElement.remove();
        editorElement = null;
        item.classList.remove("is-expanded");
        return;
      }
      editorElement = this.buildWorkflowEditor(workflow, () => {
        editorElement?.remove();
        editorElement = null;
        item.classList.remove("is-expanded");
      });
      item.appendChild(editorElement);
      item.classList.add("is-expanded");
    });

    return item;
  }

  /**
   * Inline editor for one workflow (or a blank draft when `workflow`
   * is null). Steps are one-per-line in the textarea — explicit and
   * obvious, no structured sub-rows to fight with.
   */
  private buildWorkflowEditor(
    workflow: KokoWorkflow | null,
    onClose: () => void,
  ): HTMLDivElement {
    const editor = document.createElement("div");
    editor.className = "koko-workflow-editor";

    const nameInput = document.createElement("input");
    nameInput.type = "text";
    nameInput.className = "koko-workflow-name-input";
    nameInput.placeholder = "Workflow name";
    nameInput.value = workflow?.name ?? "";

    const descInput = document.createElement("input");
    descInput.type = "text";
    descInput.className = "koko-workflow-desc-input";
    descInput.placeholder = "One-line description";
    descInput.value = workflow?.description ?? "";

    const stepsLabel = document.createElement("div");
    stepsLabel.className = "koko-workflow-steps-label";
    stepsLabel.textContent = "Steps (one per line)";

    const stepsInput = document.createElement("textarea");
    stepsInput.className = "koko-workflow-steps-input";
    stepsInput.setAttribute("spellcheck", "false");
    stepsInput.placeholder = "Open the Characters panel…";
    stepsInput.value = (workflow?.steps ?? []).join("\n");

    const toolbar = document.createElement("div");
    toolbar.className = "koko-workflow-editor-toolbar";

    const errorLabel = document.createElement("span");
    errorLabel.className = "koko-workflow-error";

    const spacer = document.createElement("span");
    spacer.style.flex = "1";

    if (workflow) {
      const deleteButton = document.createElement("button");
      deleteButton.type = "button";
      deleteButton.className = "koko-pill-button koko-workflow-delete";
      deleteButton.textContent = "DELETE";
      deleteButton.addEventListener("click", () => {
        deleteWorkflow(workflow.id);
        onClose();
      });
      toolbar.appendChild(deleteButton);
    }

    const saveButton = document.createElement("button");
    saveButton.type = "button";
    saveButton.className = "koko-pill-button is-primary koko-workflow-save";
    saveButton.textContent = "SAVE";
    saveButton.addEventListener("click", () => {
      const saved = saveWorkflow({
        id: workflow?.id,
        name: nameInput.value,
        description: descInput.value,
        steps: stepsInput.value.split("\n"),
      });
      if (!saved) {
        errorLabel.textContent = "needs a name and at least one step";
        return;
      }
      onClose();
    });

    toolbar.appendChild(errorLabel);
    toolbar.appendChild(spacer);
    toolbar.appendChild(saveButton);

    editor.appendChild(nameInput);
    editor.appendChild(descInput);
    editor.appendChild(stepsLabel);
    editor.appendChild(stepsInput);
    editor.appendChild(toolbar);
    return editor;
  }

  /**
   * Public surface used by `KokoApp` to mirror `WakeWordListener` state
   * into the panel. The persistent row tells the user *why* nothing is
   * happening — "wake word: ON" with no signal is the worst UX, since
   * the user can't tell if it's a phrase mismatch, missing permission,
   * or unsupported browser.
   */
  setWakeWordStatus(status: WakeWordStatus): void {
    if (!this.wakeWordStatusDot || !this.wakeWordStatusLabel) return;
    this.wakeWordStatusDot.dataset.kind = status.kind;
    this.wakeWordStatusLabel.textContent = describeWakeWordStatus(status);
  }

  /** Replace the rolling transcript list shown under the status row. */
  setWakeWordTranscripts(transcripts: readonly string[]): void {
    if (!this.wakeWordTranscriptsList) return;
    this.wakeWordTranscriptsList.replaceChildren();
    if (transcripts.length === 0) {
      const placeholder = document.createElement("li");
      placeholder.className = "koko-wake-transcripts-empty";
      placeholder.textContent = "(nothing heard yet)";
      this.wakeWordTranscriptsList.appendChild(placeholder);
      return;
    }
    for (const transcript of transcripts) {
      const item = document.createElement("li");
      item.textContent = transcript;
      this.wakeWordTranscriptsList.appendChild(item);
    }
  }

  private buildWakeWordStatusRow(): HTMLDivElement {
    const row = document.createElement("div");
    row.className = "koko-panel-row koko-panel-row-status";

    const labelWrap = document.createElement("div");
    labelWrap.className = "koko-panel-row-label";
    const label = document.createElement("span");
    label.textContent = "Wake status";
    const hint = document.createElement("span");
    hint.className = "koko-panel-row-hint";
    hint.textContent = "what the listener is doing right now";
    labelWrap.appendChild(label);
    labelWrap.appendChild(hint);

    const status = document.createElement("div");
    status.className = "koko-wake-status";
    const dot = document.createElement("span");
    dot.className = "koko-wake-status-dot";
    dot.dataset.kind = "idle";
    const statusLabel = document.createElement("span");
    statusLabel.className = "koko-wake-status-label";
    statusLabel.textContent = "off";
    status.appendChild(dot);
    status.appendChild(statusLabel);

    row.appendChild(labelWrap);
    row.appendChild(status);

    this.wakeWordStatusDot = dot;
    this.wakeWordStatusLabel = statusLabel;
    this.wakeWordStatusRow = row;
    return row;
  }

  private buildWakeWordTranscriptsBox(): HTMLDivElement {
    const box = document.createElement("div");
    box.className = "koko-wake-transcripts";

    const heading = document.createElement("div");
    heading.className = "koko-wake-transcripts-heading";
    heading.textContent = "recent transcripts";

    const list = document.createElement("ol");
    list.className = "koko-wake-transcripts-list";
    const placeholder = document.createElement("li");
    placeholder.className = "koko-wake-transcripts-empty";
    placeholder.textContent = "(nothing heard yet)";
    list.appendChild(placeholder);

    box.appendChild(heading);
    box.appendChild(list);

    this.wakeWordTranscriptsList = list;
    this.wakeWordTranscriptsBox = box;
    return box;
  }

  private applyWakeWordRowVisibility(): void {
    const enabled = this.options.settings.get("wakeWordEnabled") as boolean;
    const visible = enabled && this.options.wakeWordSupported;
    if (this.wakeWordStatusRow) {
      this.wakeWordStatusRow.style.display = visible ? "" : "none";
    }
    if (this.wakeWordTranscriptsBox) {
      this.wakeWordTranscriptsBox.style.display = visible ? "" : "none";
    }
  }

  private buildTitle(): HTMLDivElement {
    const title = document.createElement("div");
    title.className = "koko-panel-title";
    const statusDot = document.createElement("span");
    statusDot.className = "koko-status-dot";
    const titleLabel = document.createElement("span");
    titleLabel.textContent = "KOKO";
    title.appendChild(statusDot);
    title.appendChild(titleLabel);
    return title;
  }

  private buildToggleRow(row: {
    label: string;
    hint: string;
    settingKey: keyof BooleanSettings;
    disabled?: boolean;
  }): HTMLDivElement {
    const rowElement = document.createElement("div");
    rowElement.className = "koko-panel-row";

    const labelWrap = document.createElement("div");
    labelWrap.className = "koko-panel-row-label";
    const label = document.createElement("span");
    label.textContent = row.label;
    const hint = document.createElement("span");
    hint.className = "koko-panel-row-hint";
    hint.textContent = row.hint;
    labelWrap.appendChild(label);
    labelWrap.appendChild(hint);

    const toggle = document.createElement("input");
    toggle.type = "checkbox";
    toggle.className = "koko-toggle";
    toggle.checked = this.options.settings.get(row.settingKey) as boolean;
    toggle.disabled = row.disabled === true;
    toggle.addEventListener("change", () => {
      this.options.settings.set(row.settingKey, toggle.checked);
    });
    this.options.settings.subscribe(row.settingKey, (nextValue) => {
      toggle.checked = nextValue as boolean;
    });

    rowElement.appendChild(labelWrap);
    rowElement.appendChild(toggle);
    return rowElement;
  }

  private buildBirdStyleRow(): HTMLDivElement | null {
    if (this.options.birdStyles.length <= 1) return null;

    const rowElement = document.createElement("div");
    rowElement.className = "koko-panel-row";

    const labelWrap = document.createElement("div");
    labelWrap.className = "koko-panel-row-label";
    const label = document.createElement("span");
    label.textContent = "Bird style";
    const hint = document.createElement("span");
    hint.className = "koko-panel-row-hint";
    hint.textContent = "choose how Koko looks";
    labelWrap.appendChild(label);
    labelWrap.appendChild(hint);

    const segmented = document.createElement("div");
    segmented.className = "koko-segmented";

    const buttons = this.options.birdStyles.map((style) => {
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = style.label;
      button.addEventListener("click", () => {
        this.options.settings.set("birdStyle", style.id);
      });
      return { style, button };
    });

    const updateSelection = () => {
      const selectedId = this.resolveSelectedBirdStyleId();
      for (const { style, button } of buttons) {
        const selected = style.id === selectedId;
        button.classList.toggle("selected", selected);
        button.setAttribute("aria-pressed", String(selected));
      }
    };
    updateSelection();
    this.options.settings.subscribe("birdStyle", updateSelection);

    for (const { button } of buttons) segmented.appendChild(button);
    rowElement.appendChild(labelWrap);
    rowElement.appendChild(segmented);
    return rowElement;
  }

  private resolveSelectedBirdStyleId(): string {
    const stored = this.options.settings.get("birdStyle");
    return this.options.birdStyles.some((style) => style.id === stored)
      ? stored
      : "pixel";
  }

  private buildToggleAndStepperRow(row: {
    label: string;
    hint: string;
    toggleKey: keyof BooleanSettings;
    stepperKey: keyof NumberSettings;
    stepperMin: number;
    stepperMax: number;
    /** Increment per click. Defaults to 1. */
    stepperStep?: number;
    stepperSuffix: string;
  }): HTMLDivElement {
    const rowElement = document.createElement("div");
    rowElement.className = "koko-panel-row koko-panel-row-with-stepper";

    const labelWrap = document.createElement("div");
    labelWrap.className = "koko-panel-row-label";
    const label = document.createElement("span");
    label.textContent = row.label;
    const hint = document.createElement("span");
    hint.className = "koko-panel-row-hint";
    hint.textContent = row.hint;
    labelWrap.appendChild(label);
    labelWrap.appendChild(hint);

    const controls = document.createElement("div");
    controls.className = "koko-panel-row-controls";

    const stepper = this.buildStepper(
      row.stepperKey,
      row.stepperMin,
      row.stepperMax,
      row.stepperSuffix,
      row.stepperStep ?? 1,
    );
    const toggle = document.createElement("input");
    toggle.type = "checkbox";
    toggle.className = "koko-toggle";
    toggle.checked = this.options.settings.get(row.toggleKey) as boolean;
    toggle.addEventListener("change", () => {
      this.options.settings.set(row.toggleKey, toggle.checked);
    });
    this.options.settings.subscribe(row.toggleKey, (nextValue) => {
      toggle.checked = nextValue as boolean;
    });

    controls.appendChild(stepper);
    controls.appendChild(toggle);

    rowElement.appendChild(labelWrap);
    rowElement.appendChild(controls);
    return rowElement;
  }

  private buildStepper(
    key: keyof NumberSettings,
    min: number,
    max: number,
    suffix: string,
    step = 1,
  ): HTMLDivElement {
    const stepper = document.createElement("div");
    stepper.className = "koko-stepper";

    const decrement = document.createElement("button");
    decrement.type = "button";
    decrement.className = "koko-stepper-button";
    decrement.textContent = "−";

    const valueLabel = document.createElement("span");
    valueLabel.className = "koko-stepper-value";

    const increment = document.createElement("button");
    increment.type = "button";
    increment.className = "koko-stepper-button";
    increment.textContent = "+";

    const updateDisplay = () => {
      const value = this.options.settings.get(key) as number;
      valueLabel.textContent = `${value} ${suffix}`;
    };
    updateDisplay();
    this.options.settings.subscribe(key, updateDisplay);

    decrement.addEventListener("click", () => {
      const current = this.options.settings.get(key) as number;
      const next = Math.max(min, current - step);
      if (next !== current) this.options.settings.set(key, next);
    });
    increment.addEventListener("click", () => {
      const current = this.options.settings.get(key) as number;
      const next = Math.min(max, current + step);
      if (next !== current) this.options.settings.set(key, next);
    });

    stepper.appendChild(decrement);
    stepper.appendChild(valueLabel);
    stepper.appendChild(increment);
    return stepper;
  }

  /**
   * Action vs Suggestion mode — moved out of the page chrome and into
   * Koko's own settings (per the 2026-06-26 yap). In ACTION mode the bird
   * commits tool calls (types + clicks submit); in SUGGESTION mode it only
   * drafts into forms and lets the user commit. Backed by the studio's
   * `KokoInteractionMode` so the bridge's prompt + tool gating stay in sync.
   */
  private buildInteractionModeRow(): HTMLDivElement {
    const rowElement = document.createElement("div");
    rowElement.className = "koko-panel-row";

    const labelWrap = document.createElement("div");
    labelWrap.className = "koko-panel-row-label";
    const label = document.createElement("span");
    label.textContent = "Koko mode";
    const hint = document.createElement("span");
    hint.className = "koko-panel-row-hint";
    hint.textContent = "act = Koko commits; suggest = Koko drafts, you confirm";
    labelWrap.appendChild(label);
    labelWrap.appendChild(hint);

    const segmented = document.createElement("div");
    segmented.className = "koko-segmented";

    const makeOption = (value: KokoInteractionMode, text: string): HTMLButtonElement => {
      const option = document.createElement("button");
      option.type = "button";
      option.textContent = text;
      option.addEventListener("click", () => {
        if (getKokoInteractionMode() === value) return;
        setKokoInteractionMode(value);
      });
      return option;
    };

    const actionOption = makeOption("action", "ACT");
    const suggestOption = makeOption("suggestion", "SUGGEST");

    const applySelection = (mode: KokoInteractionMode) => {
      actionOption.classList.toggle("selected", mode === "action");
      suggestOption.classList.toggle("selected", mode === "suggestion");
    };
    applySelection(getKokoInteractionMode());
    subscribeKokoInteractionMode(applySelection);

    segmented.appendChild(actionOption);
    segmented.appendChild(suggestOption);

    rowElement.appendChild(labelWrap);
    rowElement.appendChild(segmented);
    return rowElement;
  }

  /**
   * Session memory is ALWAYS on now (no toggle). The row exposes the
   * memory CONTENT instead: "Edit" opens the memory editor so the user
   * can read / prune / hand-author what Koko remembers.
   */
  private buildMemoryRow(): HTMLDivElement {
    const rowElement = document.createElement("div");
    rowElement.className = "koko-panel-row";

    const labelWrap = document.createElement("div");
    labelWrap.className = "koko-panel-row-label";
    const label = document.createElement("span");
    label.textContent = "Session memory";
    const hint = document.createElement("span");
    hint.className = "koko-panel-row-hint";
    hint.textContent = "always on — view or edit what Koko remembers";
    labelWrap.appendChild(label);
    labelWrap.appendChild(hint);

    const controls = document.createElement("div");
    controls.className = "koko-panel-row-controls";

    const editButton = document.createElement("button");
    editButton.type = "button";
    editButton.className = "koko-pill-button koko-memory-edit-button";
    editButton.textContent = "EDIT";
    editButton.addEventListener("click", () => this.options.onEditMemory());

    controls.appendChild(editButton);

    rowElement.appendChild(labelWrap);
    rowElement.appendChild(controls);
    return rowElement;
  }

  /**
   * Director playbook (`director.md`) — visible + editable from the panel.
   * This file is Koko's directing brain; surfacing it here lets the user
   * read how Koko works and tweak its voice / pipeline / rules live. Edits
   * persist as a localStorage override (the shipped file is the default);
   * "Reset" drops the override. The section is collapsed by default so it
   * doesn't dominate the settings list.
   */
  private buildDirectorPlaybookSection(): HTMLDivElement {
    const section = document.createElement("div");
    section.className = "koko-director-section";

    const headerRow = document.createElement("div");
    headerRow.className = "koko-panel-row koko-director-header";

    const labelWrap = document.createElement("div");
    labelWrap.className = "koko-panel-row-label";
    const label = document.createElement("span");
    label.textContent = "Director playbook";
    const hint = document.createElement("span");
    hint.className = "koko-panel-row-hint koko-director-hint";
    labelWrap.appendChild(label);
    labelWrap.appendChild(hint);

    const toggleButton = document.createElement("button");
    toggleButton.type = "button";
    toggleButton.className = "koko-pill-button koko-director-toggle";
    toggleButton.textContent = "VIEW";

    headerRow.appendChild(labelWrap);
    headerRow.appendChild(toggleButton);

    const body = document.createElement("div");
    body.className = "koko-director-body";
    body.hidden = true;

    const textarea = document.createElement("textarea");
    textarea.className = "koko-director-textarea";
    textarea.setAttribute("spellcheck", "false");
    textarea.setAttribute("autocomplete", "off");
    textarea.dataset.kokoAction = "director-playbook";

    const toolbar = document.createElement("div");
    toolbar.className = "koko-director-toolbar";

    const status = document.createElement("span");
    status.className = "koko-director-status";

    const spacer = document.createElement("span");
    spacer.style.flex = "1";

    const resetButton = document.createElement("button");
    resetButton.type = "button";
    resetButton.className = "koko-pill-button koko-director-reset";
    resetButton.textContent = "RESET";

    const saveButton = document.createElement("button");
    saveButton.type = "button";
    saveButton.className = "koko-pill-button is-primary koko-director-save";
    saveButton.textContent = "SAVE";

    toolbar.appendChild(status);
    toolbar.appendChild(spacer);
    toolbar.appendChild(resetButton);
    toolbar.appendChild(saveButton);

    body.appendChild(textarea);
    body.appendChild(toolbar);

    const refreshMeta = () => {
      const customized = isDirectorPlaybookCustomized();
      hint.textContent = customized
        ? "edited — Koko follows your custom playbook"
        : "the prompt that drives Koko · default";
      status.textContent = customized ? "custom (overrides director.md)" : "default director.md";
      resetButton.disabled = !customized;
    };

    const seedTextarea = () => {
      textarea.value = readDirectorPlaybookForEditor();
    };

    let expanded = false;
    const setExpanded = (next: boolean) => {
      expanded = next;
      body.hidden = !expanded;
      toggleButton.textContent = expanded ? "HIDE" : "VIEW";
      if (expanded) seedTextarea();
    };

    toggleButton.addEventListener("click", () => setExpanded(!expanded));
    saveButton.addEventListener("click", () => {
      writeDirectorPlaybook(textarea.value);
      seedTextarea();
      refreshMeta();
    });
    resetButton.addEventListener("click", () => {
      resetDirectorPlaybook();
      seedTextarea();
      refreshMeta();
    });

    // Reflect external edits (e.g. another surface saving the playbook).
    subscribeDirectorPlaybook(() => {
      if (expanded) seedTextarea();
      refreshMeta();
    });

    refreshMeta();

    section.appendChild(headerRow);
    section.appendChild(body);
    return section;
  }

  private buildFooter(): HTMLDivElement {
    const footer = document.createElement("div");
    footer.className = "koko-panel-footer";
    const line1 = document.createElement("span");
    line1.textContent = "/ to type · ctrl+space to talk";
    const line2 = document.createElement("span");
    line2.textContent = "ctrl+shift+t toggles text-only mode";
    footer.appendChild(line1);
    footer.appendChild(line2);
    return footer;
  }

  private startPositionTimer(): void {
    if (this.positionTimerId !== null) return;
    this.positionTimerId = window.setInterval(
      () => this.positionToAnchor(),
      POSITION_TICK_MS,
    );
  }

  private stopPositionTimer(): void {
    if (this.positionTimerId !== null) {
      window.clearInterval(this.positionTimerId);
      this.positionTimerId = null;
    }
  }

  private positionToAnchor(): void {
    const anchor = this.options.getAnchorPoint();
    const panelWidth = this.panelElement.offsetWidth || 360;
    const panelHeight = this.panelElement.offsetHeight || 520;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let rightEdge = anchor.x;
    let leftEdge = rightEdge - panelWidth;
    if (leftEdge < 8) leftEdge = 8;
    leftEdge = Math.min(viewportWidth - panelWidth - 8, leftEdge);

    let topEdge = anchor.y - panelHeight - PANEL_GAP_ABOVE_ANCHOR_PX;
    if (topEdge < 8) {
      // Panel doesn't fit above the anchor. Flip below the anchor if
      // there's room; otherwise pin to the top edge of the viewport.
      // The panel's `max-height + overflow-y: auto` handles cases
      // where the content is taller than the viewport so the user
      // can scroll to reach later rows. Letting topEdge go negative
      // would clip the first row, which is the most important.
      const belowAnchor = anchor.y + PANEL_GAP_ABOVE_ANCHOR_PX;
      topEdge = belowAnchor + panelHeight <= viewportHeight - 8 ? belowAnchor : 8;
    }

    this.panelElement.style.left = `${leftEdge}px`;
    this.panelElement.style.top = `${topEdge}px`;
  }

  private installDismissListeners(): void {
    if (this.outsideClickListener || this.keyDownListener) return;
    this.outsideClickListener = (event) => {
      const path = event.composedPath();
      if (!path.includes(this.panelElement)) {
        this.hide();
      }
    };
    this.keyDownListener = (event) => {
      if (event.key === "Escape") this.hide();
    };
    window.setTimeout(() => {
      if (this.outsideClickListener) {
        document.addEventListener("mousedown", this.outsideClickListener, true);
      }
      if (this.keyDownListener) {
        document.addEventListener("keydown", this.keyDownListener);
      }
    }, 0);
  }

  private removeDismissListeners(): void {
    if (this.outsideClickListener) {
      document.removeEventListener("mousedown", this.outsideClickListener, true);
      this.outsideClickListener = null;
    }
    if (this.keyDownListener) {
      document.removeEventListener("keydown", this.keyDownListener);
      this.keyDownListener = null;
    }
  }
}

function describeWakeWordStatus(status: WakeWordStatus): string {
  switch (status.kind as WakeWordStatusKind) {
    case "idle":
      return "off";
    case "starting":
      return "starting…";
    case "listening":
      return "listening";
    case "restarting":
      return "restarting…";
    case "blocked":
      return status.reason ? `blocked: ${status.reason}` : "blocked";
  }
}

// Type utilities so row-builders can safely key into settings. TypeScript
// infers the boolean/number subsets from the KokoSettings shape.
type BooleanSettings = {
  [K in keyof import("../app/SettingsStore").KokoSettings as import("../app/SettingsStore").KokoSettings[K] extends boolean
    ? K
    : never]: import("../app/SettingsStore").KokoSettings[K];
};
type NumberSettings = {
  [K in keyof import("../app/SettingsStore").KokoSettings as import("../app/SettingsStore").KokoSettings[K] extends number
    ? K
    : never]: import("../app/SettingsStore").KokoSettings[K];
};
